import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/authContext';
import { Box, Paper, Button, Typography, IconButton, InputAdornment, Collapse } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { TextInput } from '../../components/inputs';
import { PrimaryButton } from '../../components/buttons';
import { apiPost, handleApiError } from '../../services/apiClient';
import { buildApiUrl, API_ENDPOINTS } from '../../config/apiEndpoints';
import { useTranslation } from 'react-i18next';

const SignUpPage: React.FC = () => {
  const { t } = useTranslation();

  const [name, setName] = useState('');
  const [accountName, setAccountName] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const auth = useContext(AuthContext);
  const navigate = useNavigate();

  const validate = () => {
    if (!accountName.trim()) {
      setError(t('Email is required'));
      return false;
    }
    if (!password) {
      setError(t('Password is required'));
      return false;
    }
    if (password.length < 8) {
      setError(t('Password must be at least 8 characters long'));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setError('');

    try {
      await apiPost(
        buildApiUrl(API_ENDPOINTS.users.register),
        {
          account_name: accountName,
          password,
          ...(name.trim() ? { name: name.trim() } : {}),
        },
        { skipAuth: true }
      );

      const response = await apiPost(
        buildApiUrl(API_ENDPOINTS.users.login),
        {
          account_name: accountName,
          password,
        },
        { skipAuth: true }
      );

      const data = await response.json();
      const token = data.result.jwt;

      auth?.handleLogin(accountName, token);
      navigate('/dashboard');
    } catch (err: any) {
      if (err?.errorData?.error?.errorKey === 'client.rateLimit') {
        setError(t('Too many requests. Please try again later.'));
        return;
      }

      switch (err?.errorData?.error?.additionalInfo) {
        case 'client.extended.linkedAccountExists':
          setError(t('An account with this email already exists.'));
          break;

        case 'client.extended.domainNotAllowed':
          setError(t('Your email domain is not recognized! Please try again or contact your company.'));
          break;

        case 'client.extended.signupDisabled':
          setError(t('Sign up is currently disabled. Please contact your company.'));
          break;

        case 'client.extended.notConfirmed':
          navigate('/login', {
            state: { notice: t('Your account is created successfully! Please check your email!') }
          });
          return;

        default:
          handleApiError(err, t('Failed to sign up'));
          break;
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'Inter, sans-serif',
        backgroundColor: 'background.lightGrey',
        padding: { xs: 1, sm: 2 },
        position: 'relative',
      }}>
      <Paper
        elevation={8}
        sx={{
          width: '100%',
          maxWidth: { xs: '100%', sm: 420 },
          padding: { xs: 2, sm: 3, md: 4 },
          backgroundColor: 'background.white',
          borderRadius: 1.5,
          textAlign: 'center',
          position: 'relative',

        }}>
        <Box
          component="img"
          src="/black-logo.png"
          alt="Company Logo"
          sx={{
            display: 'block',
            mx: 'auto',
            mb: { xs: 2, sm: 3 },
            width: { xs: 100, sm: 120, md: 140 },
            height: 'auto',
            position: 'relative',
            zIndex: 1,
            border: 'none',
            outline: 'none',
            backgroundColor: 'transparent',
          }}
        />

        <Typography
          variant="h5"
          component="h2"
          color="text.primary"
          mb={{ xs: 3, sm: 4 }}
          sx={{
            fontWeight: 600,
            color: 'text.darkGrey',
            position: 'relative',
            zIndex: 1,
            fontSize: (theme: any) => theme.typography.fontSizes.heading2,

          }}
        >
          {t('Create your account')}
        </Typography>

        <Box component="form" onSubmit={handleSubmit} sx={{ position: 'relative', zIndex: 1 }}>
          <TextInput
            fullWidth
            id="name"
            label={t('Name')}
            value={name}
            onChange={(e) => { setName(e.target.value); setError(''); }}
            placeholder={t('Your name')}
            sx={{ mb: 2, fontSize: (theme: any) => theme.typography.fontSizes.body }}
          />

          <TextInput
            fullWidth
            id="account"
            label={t('Email')}
            value={accountName}
            onChange={(e) => { setAccountName(e.target.value); setError(''); }}
            placeholder="email@example.com"
            sx={{ mb: 2, fontSize: (theme: any) => theme.typography.fontSizes.body }}
          />

          <TextInput
            fullWidth
            id="password"
            label={t('Password')}
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError(''); }}
            placeholder="••••••••"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    size="small"
                    sx={{
                      color: 'text.light',
                      opacity: 0.6,
                      '&:hover': {
                        opacity: 0.8,
                        backgroundColor: 'transparent'
                      },
                      '& .MuiSvgIcon-root': {
                        fontSize: (theme: any) => theme.typography.fontSizes.heading
                      }
                    }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 4 }}
          />

          <Collapse in={!!error}>
            <Typography
              variant="body2"
              color="warning.main"
              sx={{ mb: 2, textAlign: 'center', fontWeight: 500 }}
            >
              {error}
            </Typography>
          </Collapse>

          <PrimaryButton
            type="submit"
            fullWidth
            loading={loading}
            disabled={!!error}
          >
            {t('Sign up')}
          </PrimaryButton>

          <Box sx={{ textAlign: 'center', mt: { xs: 1.5, sm: 2 } }}>
            <Typography variant="body2" sx={{ color: 'text.grey', display: 'inline' }}>
              {t('Already have an account?')}{' '}
            </Typography>
            <Button
              variant="text"
              onClick={() => navigate('/login')}
              sx={{
                color: 'text.dark',
                textTransform: 'none',
                fontSize: (theme: any) => theme.typography.fontSizes.small,
                fontWeight: 600,
                p: 0,
                minWidth: 'auto',
                '&:hover': {
                  backgroundColor: 'transparent',
                  textDecoration: 'underline',
                },
              }}
            >
              {t('Sign in')}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default SignUpPage;
